import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { X } from 'lucide-react-native';
import { memo, useEffect, useState } from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  TextInput,
  View,
} from 'react-native';
import {
  initialWindowMetrics,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { voiceInputColors } from '../voiceInputColors';

const kBox = '#2C2C2E';

/** Spoken words opened to edit before they go: 发送 sends, ✕ drops them. */
export const VoiceEditor = memo(
  ({
    text,
    canSend,
    onSend,
    onClose,
  }: {
    /** Null: closed. */
    text: string | null;
    canSend: boolean;
    onSend: (text: string) => void;
    onClose: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    // Full screen over everything: a host that zeroes the top inset (the
    // floating chat panel) must not put ✕ under the status bar.
    const top = Math.max(insets.top, initialWindowMetrics?.insets.top ?? 0);
    const [value, setValue] = useState(text ?? '');
    useEffect(() => {
      if (text != null) setValue(text);
    }, [text]);
    const ready = canSend && !!value.trim();
    return (
      <Modal
        visible={text != null}
        animationType="fade"
        transparent
        onRequestClose={onClose}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={{
            flex: 1,
            backgroundColor: '#000',
            paddingTop: top + rpx(8),
            paddingHorizontal: rpx(16),
          }}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="关闭"
            hitSlop={rpx(10)}
            onPress={onClose}
            style={{ alignSelf: 'flex-start', padding: rpx(4) }}
          >
            <X size={rpx(26)} color={voiceInputColors.primaryForeground} />
          </Pressable>
          <View style={{ flex: 1, justifyContent: 'flex-end', gap: rpx(16) }}>
            <View
              style={{
                borderRadius: rpx(24),
                paddingHorizontal: rpx(20),
                paddingVertical: rpx(16),
                backgroundColor: kBox,
              }}
            >
              <TextInput
                value={value}
                onChangeText={setValue}
                multiline
                autoFocus
                // No lineHeight: iOS puts a multiline input's extra line
                // height above the text until it is edited, so the words
                // first sit low in the box.
                style={{
                  maxHeight: rpx(240),
                  fontSize: rpx(18),
                  color: voiceInputColors.primaryForeground,
                  padding: 0,
                }}
              />
            </View>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                paddingBottom: rpx(16),
              }}
            >
              <Pressable
                accessibilityRole="button"
                disabled={!ready}
                onPress={() => onSend(value.trim())}
                style={{
                  minWidth: rpx(120),
                  height: rpx(44),
                  borderRadius: rpx(22),
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: voiceInputColors.primary,
                  opacity: ready ? 1 : 0.4,
                }}
              >
                <Typography.Text
                  size={rpx(16)}
                  medium
                  color={voiceInputColors.primaryForeground}
                >
                  发送
                </Typography.Text>
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    );
  },
);
